/**
 * Debug logger utility to help with tracking language-related operations
 * 
 * This can be used to track language preference changes and URL redirections
 * without cluttering the console with logs in production.
 */

const DEBUG_MODE = process.env.NODE_ENV === 'development';

interface DebugLogger {
  log: (module: string, message: string, data?: unknown) => void;
  error: (module: string, message: string, error?: unknown) => void;
}

const noopLogger: DebugLogger = {
  log: () => {},
  error: () => {},
};

const consoleLogger: DebugLogger = {
  log: (module: string, message: string, data?: unknown) => {
    if (data) {
      console.log(`[${module}] ${message}`, data);
    } else {
      console.log(`[${module}] ${message}`);
    }
  },
  error: (module: string, message: string, error?: unknown) => {
    if (error) {
      console.error(`[${module}] ERROR: ${message}`, error);
    } else {
      console.error(`[${module}] ERROR: ${message}`);
    }
  },
};

export const debugLogger = DEBUG_MODE ? consoleLogger : noopLogger;

// Create specialized loggers for specific modules
export const createLogger = (moduleName: string) => {
  return {
    log: (message: string, data?: unknown) => debugLogger.log(moduleName, message, data),
    error: (message: string, error?: unknown) => debugLogger.error(moduleName, message, error),
  };
};

// Pre-defined loggers for common modules
export const languageLogger = createLogger('Language');
export const authLogger = createLogger('Auth');
export const routerLogger = createLogger('Router'); 